import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OpeningAllocationReversal } from './OpeningAllocationReversal';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Invoice, InvoicePayment, InvoicePayments as InvoicePaymentsData } from '../models/table-models';
import { addInvoicePayment, deleteInvoicePayment, getInvoicePayments, useGetDocument } from '../utils/clients-hooks';
import { resourceCache } from 'use-async-resource';
import { getCurrencySymbol, transformDateToString } from '../utils/helper-functions';
import { CustomToastContent } from './CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';

/**
 * Плащанията по банкова фактура.
 *
 * Сметките идват от сървъра, не от реда, през който е отворен екранът: merge-натата фактура има
 * няколко реда и списъкът не носи задължително всички, а платеното се мени точно оттук и подадено
 * веднъж, остарява.
 *
 * Сумите пътуват в стотинки, както total на фактурата, а се показват и въвеждат в основната валута.
 */
export const InvoicePayments = ({
    invoice,
    refetch,
    onInvoiceClosed,
    readOnly = false,
}: {
    invoice: Invoice;
    refetch?: () => void;
    /** Вика се, когато плащането е покрило остатъка - диалогът над него няма какво повече да показва. */
    onInvoiceClosed?: () => void;
    /**
     * Само за преглед: по фактурата е издадено кредитно известие, тоест нови плащания и изтриване на
     * въведените сървърът отказва. Историята остава видима - тя обяснява защо фактурата не е за прихващане.
     */
    readOnly?: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { t: openingT } = useTranslation('translation', { keyPrefix: 'openingBalances' });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });

    const [data, setData] = useState<InvoicePaymentsData>();
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [saving, setSaving] = useState(false);

    const [amountCents, setAmountCents] = useState(0);
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
    const [comment, setComment] = useState('');

    const payments: InvoicePayment[] = data?.payments ?? [];
    const total = data?.total ?? 0;
    const paidAmount = data?.paidAmount ?? 0;
    const remaining = data?.remaining ?? 0;
    const isPaid = data?.paid ?? false;
    const symbol = getCurrencySymbol(data?.currency ?? invoice.currency);
    // Номерът идва от документа, а не от реда: при обединена фактура двата са един и същ, но редът
    // може и да няма зареден документ.
    const invoiceNumber = data?.invoiceNumber ?? invoice.document?.invoiceNumber;
    const inMainUnit = (cents: number) => (cents / 100).toFixed(2);

    const load = useCallback(() => {
        if (!invoice.id) return;
        setLoading(true);
        getInvoicePayments(invoice.id)
            .then(setData)
            .finally(() => setLoading(false));
    }, [invoice.id]);

    useEffect(load, [load]);

    /**
     * Полето винаги показва сума с две цифри и се пълни отдясно наляво, като на ПОС терминал: първата
     * натисната цифра са стотинки, следващата ги изтласква наляво. Така "125" става 1.25, а празно
     * поле не съществува - стойността е 0.00.
     *
     * Показаното се извежда от броя стотинки, тоест цифрите в него СА стойността. Затова новата
     * стойност се смята просто като цифрите на въведеното: "0.00" + натиснато "1" в края дава "0.001",
     * а от него - 1 стотинка. Backspace работи по същия начин, отзад напред: от "1.25" остава "0.12".
     */
    const amountRef = useRef<HTMLInputElement>(null);
    const amount = inMainUnit(amountCents);

    // Курсорът стои в края, за да пише следващата цифра там, където потребителят я очаква.
    useEffect(() => {
        const input = amountRef.current;
        if (input && document.activeElement === input) {
            input.setSelectionRange(input.value.length, input.value.length);
        }
    }, [amountCents]);

    const changeAmount = (raw: string) => {
        // Отрязването пази Number от преливане при задържан клавиш; 11 цифри са над 99 милиона.
        const digits = raw.replace(/\D/g, '').slice(-11);
        setAmountCents(digits === '' ? 0 : Number(digits));
    };

    const closeDialog = () => {
        setShowDialog(false);
        setAmountCents(0);
        setComment('');
        setPaymentDate(new Date().toISOString().slice(0, 10));
    };

    const submit = async () => {
        if (!invoice.id || amountCents <= 0) return;
        setSaving(true);
        try {
            await addInvoicePayment(invoice.id, {
                amount: amountCents,
                paymentDate,
                comment,
            });
            resourceCache(useGetDocument).clear();
            closeDialog();
            // Състоянието идва от сървъра, а не от сметка отстрани: там се решава дали остатъкът е
            // покрит, включително при закръгляне на последната стотинка.
            const fresh = await getInvoicePayments(invoice.id);
            setData(fresh);
            refetch?.();
            toast.success(<CustomToastContent content={[t('paymentAdded')]} />, toastOptionsTopRight);
            if (fresh?.paid) {
                onInvoiceClosed?.();
            }
        } finally {
            setSaving(false);
        }
    };

    const remove = async (payment: InvoicePayment) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm(t('deletePaymentConfirm'))) return;
        await deleteInvoicePayment(payment.id, t('deletePaymentComment'));
        resourceCache(useGetDocument).clear();
        load();
        refetch?.();
    };

    return (
        <div className='w-100 mb-3 pb-3 border-bottom'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <h6 className='m-0'>
                    {invoiceNumber != null ? t('paymentsForInvoice', { number: invoiceNumber }) : t('payments')}
                </h6>
                {!isPaid && !readOnly && !loading && (
                    <Button size='sm' variant='success' onClick={() => setShowDialog(true)}>
                        {t('addPayment')}
                    </Button>
                )}
            </div>

            <div className='d-flex mb-2'>
                <div className='me-4'>
                    {t('total')}: <strong>{inMainUnit(total)} {symbol}</strong>
                </div>
                <div className='me-4'>
                    {t('paid')}: <strong>{inMainUnit(paidAmount)} {symbol}</strong>
                </div>
                <div>
                    {t('remaining')}:{' '}
                    <strong className={remaining > 0 ? 'text-danger' : 'text-success'}>
                        {inMainUnit(remaining)} {symbol}
                    </strong>
                </div>
            </div>

            {loading ? (
                <Spinner animation='border' size='sm' />
            ) : payments.length === 0 ? (
                <div className='text-muted'>{t('noPayments')}</div>
            ) : (
                <table className='table table-sm table-bordered mb-0'>
                    <thead>
                        <tr>
                            <th>{t('paymentDate')}</th>
                            <th>{t('amount')}</th>
                            <th>{t('comment')}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map((payment) => (
                            <tr key={payment.id}>
                                <td>{transformDateToString(payment.paymentDate)}</td>
                                <td>{inMainUnit(payment.amount)} {symbol}</td>
                                <td>{payment.comment}</td>
                                <td>
                                    {!isPaid && !readOnly && (
                                        <Button size='sm' variant='outline-danger' onClick={() => remove(payment)}>
                                            {t('delete')}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!!data?.openingAllocations?.length && (
                <div className='mt-3'>
                    <h6>{openingT('creditHistory')}</h6>
                    <table className='table table-sm table-bordered'>
                        <thead><tr><th>{commonT('date')}</th><th>{openingT('amount')}</th><th>{openingT('creditColumn')}</th><th>{commonT('comment')}</th></tr></thead>
                        <tbody>{data.openingAllocations.map(allocation => (
                            <tr key={allocation.id}>
                                <td>{transformDateToString(allocation.paymentDate)}</td>
                                <td>{inMainUnit(allocation.amount)} {symbol}</td>
                                <td>#{allocation.creditId}</td><td>{allocation.comment}<OpeningAllocationReversal settlement={allocation} onReversed={() => { load(); refetch?.(); }} /></td>
                            </tr>
                        ))}</tbody>
                    </table>
                    <small className='text-muted'>{openingT('creditHistoryHint')}</small>
                </div>
            )}

            <Modal show={showDialog} onHide={closeDialog} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('addPayment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className='mb-3'>
                        <Form.Label>{t('amount')} ({symbol})</Form.Label>
                        <Form.Control
                            type='text'
                            inputMode='numeric'
                            ref={amountRef}
                            value={amount}
                            onChange={(e) => changeAmount(e.target.value)}
                            onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        />
                        <Form.Text muted>
                            {t('remaining')}: {inMainUnit(remaining)} {symbol}
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className='mb-3'>
                        <Form.Label>{t('paymentDate')}</Form.Label>
                        <Form.Control type='date' value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>{t('comment')}</Form.Label>
                        <Form.Control type='text' value={comment} onChange={(e) => setComment(e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={closeDialog}>
                        {t('cancelPayment')}
                    </Button>
                    <Button
                        variant='success'
                        onClick={submit}
                        disabled={saving || amountCents <= 0 || amountCents > remaining}
                    >
                        {t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
